import { useState } from 'react';
import Modal from '../shared/Modal';
import Btn from '../shared/Btn';
import Field from '../shared/Field';
import Input from '../shared/Input';
import Select from '../shared/Select';
import Grid from '../shared/Grid';
import { createPatient } from '../../../api/claimsClient';
import { suggestPatientAccountNumber } from '../../../utils/patientAccountNumber';

const GENDER_OPTIONS = [
  { value: '', label: '—' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_OPTIONS = [
  { value: 'self', label: 'Self (subscriber = patient)' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'other', label: 'Other' },
];

const SUB_GENDER_OPTIONS = [
  { value: '', label: '—' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'U', label: 'Other' },
];

/**
 * Backed by existing PatientSerializer requirements:
 * - Required: first_name, last_name, date_of_birth, gender, address_line1, city, state, zip_code,
 *   AND at least one of email|phone.
 *
 * Insurance subsection is intentionally absent in v1 (insurance_records is read-only on the serializer).
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onCreated: (patient: any) => void,
 * }} props
 */
export default function NewPatientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', middle_name: '', name_suffix: '',
    date_of_birth: '', gender: '',
    phone: '', email: '',
    address_line1: '', address_line2: '',
    city: '', state: 'CA', zip_code: '',
    ssn: '',
    account_number: '',
  });
  const [insurance, setInsurance] = useState({
    insurance_company: '',
    group_number: '',
    policy_number: '',
    subscriber_relationship: 'self',
    subscriber_member_id: '',
    subscriber_first_name: '',
    subscriber_last_name: '',
    subscriber_middle_name: '',
    subscriber_date_of_birth: '',
    subscriber_gender: '',
    subscriber_address_line_1: '',
    subscriber_address_line_2: '',
    subscriber_city: '',
    subscriber_state: '',
    subscriber_zip_code: '',
    subscriber_phone: '',
  });
  const [errors, setErrors] = useState(/** @type {any} */ (null));
  const [busy, setBusy] = useState(false);

  const upd = (/** @type {string} */ k) => (/** @type {any} */ e) =>
    setForm((/** @type {any} */ f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));
  const updIns = (/** @type {string} */ k) => (/** @type {any} */ e) =>
    setInsurance((/** @type {any} */ f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const submit = async () => {
    /** @type {Record<string, string>} */
    const err = {};
    for (const f of ['first_name', 'last_name', 'date_of_birth', 'gender', 'address_line1', 'city', 'state', 'zip_code']) {
      if (!(/** @type {any} */ (form))[f]) err[f] = 'Required';
    }
    if (!form.email && !form.phone) err.phone = 'Email or phone required';
    if (Object.keys(err).length > 0) { setErrors(err); return; }
    setErrors(null); setBusy(true);
    // Only send insurance payload if at least one field is filled. Empty
    // payload would create an empty PatientInsurance row, which is noise.
    const hasInsurance = Object.entries(insurance).some(([k, v]) => k !== 'subscriber_relationship' && v);
    // Strip empty strings from the insurance object so optional fields (like
    // subscriber_date_of_birth) are omitted rather than sent as '' which
    // DRF's DateField rejects as an invalid format.
    const insurancePayload = Object.fromEntries(
      Object.entries(insurance).filter(([, v]) => v !== ''),
    );
    const payload = hasInsurance ? { ...form, insurance: insurancePayload } : form;
    try {
      const patient = await createPatient(payload);
      onCreated?.(patient);
      onClose?.();
    } catch (/** @type {any} */ e) {
      setErrors(e?.response?.data || { detail: e?.message || 'Create failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} title="New Patient" width={640}
      footer={<>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" icon="user-plus" onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create & Link'}
        </Btn>
      </>}
    >
      <Grid cols={2}>
        <Field label="First name" required error={errors?.first_name}>
          <Input value={form.first_name} onChange={upd('first_name')} />
        </Field>
        <Field label="Last name" required error={errors?.last_name}>
          <Input value={form.last_name} onChange={upd('last_name')} />
        </Field>
        <Field label="Middle name"><Input value={form.middle_name} onChange={upd('middle_name')} /></Field>
        <Field label="Suffix"><Input value={form.name_suffix} onChange={upd('name_suffix')} maxLength={10} /></Field>
        <Field label="Date of birth" required error={errors?.date_of_birth}>
          <Input type="date" value={form.date_of_birth} onChange={upd('date_of_birth')} />
        </Field>
        <Field label="Gender" required error={errors?.gender}>
          <Select value={form.gender} options={GENDER_OPTIONS} onChange={upd('gender')} />
        </Field>
        <Field label="Phone" error={errors?.phone}>
          <Input value={form.phone} onChange={upd('phone')} />
        </Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={upd('email')} /></Field>
        <Field label="Address" required error={errors?.address_line1}>
          <Input value={form.address_line1} onChange={upd('address_line1')} />
        </Field>
        <Field label="Address line 2"><Input value={form.address_line2} onChange={upd('address_line2')} /></Field>
        <Field label="City" required error={errors?.city}>
          <Input value={form.city} onChange={upd('city')} />
        </Field>
        <Field label="State" required error={errors?.state}>
          <Input value={form.state} onChange={upd('state')} maxLength={2} />
        </Field>
        <Field label="ZIP" required error={errors?.zip_code}>
          <Input value={form.zip_code} onChange={upd('zip_code')} />
        </Field>
        <Field label="SSN (optional)"><Input value={form.ssn} onChange={upd('ssn')} maxLength={11} /></Field>
        <Field label="Patient account # (optional)" caption="Auto-fills CMS-1500 Box 26 on every claim for this patient">
          <div style={{ display: 'flex', gap: 6 }}>
            <Input value={form.account_number} onChange={upd('account_number')} maxLength={80} />
            <Btn
              kind="ghost"
              size="sm"
              icon="sparkles"
              type="button"
              onClick={() => upd('account_number')(suggestPatientAccountNumber(form))}
              disabled={busy}
            >
              Generate
            </Btn>
          </div>
        </Field>
      </Grid>

      <div style={{
        marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--ab-border-hair)',
        fontSize: 12, fontWeight: 'var(--ab-weight-semi)',
        color: 'var(--ab-fg-subtle)', textTransform: 'uppercase',
        letterSpacing: 'var(--ab-tracking-label)', marginBottom: 12,
      }}>
        Insurance & Subscriber (optional — auto-fills CMS-1500 Boxes 1a, 4, 7, 11)
      </div>
      <Grid cols={2}>
        <Field label="Insurance company">
          <Input value={insurance.insurance_company} onChange={updIns('insurance_company')} />
        </Field>
        <Field label="Member ID (Box 1a)">
          <Input value={insurance.subscriber_member_id} onChange={updIns('subscriber_member_id')} maxLength={80} />
        </Field>
        <Field label="Group #">
          <Input value={insurance.group_number} onChange={updIns('group_number')} maxLength={100} />
        </Field>
        <Field label="Policy #">
          <Input value={insurance.policy_number} onChange={updIns('policy_number')} maxLength={100} />
        </Field>
        <Field label="Subscriber relationship to patient" caption="If 'self', subscriber identity defaults to the patient">
          <Select
            value={insurance.subscriber_relationship}
            options={RELATIONSHIP_OPTIONS}
            onChange={updIns('subscriber_relationship')}
          />
        </Field>
        <div />
        {insurance.subscriber_relationship !== 'self' && (
          <>
            <Field label="Subscriber first name">
              <Input value={insurance.subscriber_first_name} onChange={updIns('subscriber_first_name')} />
            </Field>
            <Field label="Subscriber last name">
              <Input value={insurance.subscriber_last_name} onChange={updIns('subscriber_last_name')} />
            </Field>
            <Field label="Subscriber DOB">
              <Input type="date" value={insurance.subscriber_date_of_birth} onChange={updIns('subscriber_date_of_birth')} />
            </Field>
            <Field label="Subscriber gender">
              <Select value={insurance.subscriber_gender} options={SUB_GENDER_OPTIONS} onChange={updIns('subscriber_gender')} />
            </Field>
            <Field label="Subscriber address">
              <Input value={insurance.subscriber_address_line_1} onChange={updIns('subscriber_address_line_1')} />
            </Field>
            <Field label="Subscriber phone">
              <Input value={insurance.subscriber_phone} onChange={updIns('subscriber_phone')} />
            </Field>
            <Field label="Subscriber city">
              <Input value={insurance.subscriber_city} onChange={updIns('subscriber_city')} />
            </Field>
            <Field label="Subscriber state / ZIP">
              <div style={{ display: 'flex', gap: 8 }}>
                <Input value={insurance.subscriber_state} onChange={updIns('subscriber_state')} maxLength={2} style={{ width: 60 }} />
                <Input value={insurance.subscriber_zip_code} onChange={updIns('subscriber_zip_code')} maxLength={10} />
              </div>
            </Field>
          </>
        )}
      </Grid>

      {errors?.detail && (
        <div style={{ color: 'var(--ab-denied)', fontSize: 12, marginTop: 12 }}>{errors.detail}</div>
      )}
    </Modal>
  );
}
